/**
 * ChatShared – publik read-only vy av en delad chatt-session.
 * URL: /chattdelad/:token
 */
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, User, Sparkles, ArrowLeft, ExternalLink, MessageSquare } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";

export default function ChatShared() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/laws/chat/shared/${token}`);
        setData(data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Chatten kunde inte hittas.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <MessageSquare className="w-10 h-10 mx-auto text-zinc-300 mb-4" />
        <h1 className="text-xl font-semibold mb-2" style={{ fontFamily: "Outfit" }}>
          Chatten kunde inte visas
        </h1>
        <p className="text-sm text-zinc-600 mb-6">{error}</p>
        <Link
          to="/lag/pbl"
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-sm text-sm font-medium hover:bg-blue-700"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Bläddra i PBL istället
        </Link>
      </div>
    );
  }

  const { sfs, chapter, paragraph, paragraph_text, paragraph_source, messages, shared_at } = data;
  const sharedDate = shared_at ? new Date(shared_at).toLocaleDateString("sv-SE") : "";
  // OG-image serveras av backend, cachad efter första anropet (public + 7 days max-age)
  const ogImageUrl = `${process.env.REACT_APP_BACKEND_URL}/api/laws/og/chat/${token}.png`;

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <Seo
        title={`Delad AI-chatt om ${sfs} ${chapter}:${paragraph} §`}
        description={`Delat AI-samtal om plan- och bygglagen ${chapter} kap. ${paragraph} §. AI-drivet av Bygglovskompassen.se.`}
        path={`/chattdelad/${token}`}
        image={ogImageUrl}
        noindex
      />

      <Link
        to={`/lag/pbl/${chapter}`}
        className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-blue-700 mb-4"
      >
        <ArrowLeft className="w-3 h-3" /> Öppna paragrafen i PBL-läsaren
      </Link>

      <div className="mb-2">
        <span className="inline-block px-2.5 py-0.5 bg-blue-100 text-blue-800 text-[10px] uppercase tracking-widest rounded-full font-semibold">
          Delad AI-chatt
        </span>
      </div>

      <h1
        className="text-3xl sm:text-4xl font-bold mb-2 text-zinc-900"
        style={{ fontFamily: "Outfit" }}
      >
        {sfs} {chapter} kap. {paragraph} §
      </h1>
      <p className="text-xs text-zinc-500 mb-6">Delat {sharedDate} · Sparat läge, uppdateras inte</p>

      {/* Paragraf-kontext */}
      {paragraph_text && (
        <section className="border-l-4 border-zinc-300 bg-zinc-50 px-4 py-3 mb-8 rounded-r-sm">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1">
            Paragrafen
          </div>
          <p className="text-sm text-zinc-800 leading-relaxed whitespace-pre-line">{paragraph_text}</p>
          {paragraph_source && (
            <a
              href={paragraph_source}
              target="_blank"
              rel="noreferrer"
              className="text-[11px] text-blue-700 hover:underline mt-2 inline-flex items-center gap-1"
            >
              Källa: Riksdagen <ExternalLink className="w-2.5 h-2.5" />
            </a>
          )}
        </section>
      )}

      {/* Chat-historik */}
      <section className="space-y-4" data-testid="shared-chat-messages">
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
            <div
              className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${
                m.role === "user" ? "bg-zinc-800 text-white" : "bg-blue-600 text-white"
              }`}
            >
              {m.role === "user" ? <User className="w-3.5 h-3.5" /> : <Sparkles className="w-3.5 h-3.5" />}
            </div>
            <div
              className={`max-w-[80%] rounded-sm px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-zinc-800 text-white"
                  : "bg-blue-50 border border-blue-100 text-zinc-800"
              }`}
            >
              {m.text}
            </div>
          </div>
        ))}
      </section>

      {/* CTA – uppmuntra vidare */}
      <div className="mt-10 border border-zinc-200 rounded-sm p-6 text-center bg-white">
        <MessageSquare className="w-6 h-6 mx-auto text-blue-500 mb-3" />
        <h2 className="text-lg font-semibold mb-2" style={{ fontFamily: "Outfit" }}>
          Vill du ställa dina egna frågor?
        </h2>
        <p className="text-sm text-zinc-600 mb-4 max-w-md mx-auto">
          Öppna paragrafen i vår PBL-läsare och chatta gratis med AI-rådgivaren om vad reglerna innebär för just ditt projekt.
        </p>
        <Link
          to={`/lag/pbl/${chapter}`}
          className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-sm text-sm font-medium"
        >
          Öppna PBL {chapter} kap.
        </Link>
      </div>
    </div>
  );
}
